/**
 * TA-1289 D09-consults, G44: the owner's call summary after a consult (AB 10:
 * "Consult to call-summary to outcome label to auto-learn"; AB line 38
 * sales:call-summary). Pure; the writer is `recordCallSummary` (packages/db
 * appointments.ts) and the owner's command is `pnpm consults summary`.
 *
 * CODES, NEVER TEXT (CLAUDE.md section 10). The summary is one next step from
 * the closed list below and an optional calendar day for it. There is no note
 * field, no summary sentence and no customer word: the event log survives
 * erasure, and a free-text summary is exactly the transcript the save-gate
 * refuses (section 6).
 *
 * BESIDE R8.5, NOT IN PLACE OF IT. `consult.summary_written` (agents
 * consultSummaryHandler.ts, flag off) is a MODEL's reading of the owner's
 * appointment notes. This is the OWNER's own record, typed, with no model and
 * no flag. The steps are R8.5's action kinds (pinned by the test) with
 * `other` dropped (a code that says nothing is not a next step) and `none`
 * added (a consult can end with nothing to do). Neither feeds an outcome label
 * or a learner yet: that step of the contract is Kabir's (decision rows 181
 * and 183), so this records and reads nothing back.
 *
 * ONLY AN ATTENDED CONSULT. A booked or confirmed consult has not happened, and
 * a no-show had no call: a summary of either would be invented.
 */

/**
 * The owner's next step after a consult, a closed list (TA-1289 G44, Kabir to
 * ratify the list; the R8.5 vocabulary it mirrors is TA-1262's).
 */
// AWAITING RATIFICATION
export const CALL_SUMMARY_NEXT_STEPS = [
  "follow_up_message",
  "send_quote",
  "source_piece",
  "hold_piece",
  "schedule_next",
  "none",
] as const;

export type CallSummaryNextStep = (typeof CALL_SUMMARY_NEXT_STEPS)[number];

export function isCallSummaryNextStep(value: unknown): value is CallSummaryNextStep {
  return typeof value === "string" && (CALL_SUMMARY_NEXT_STEPS as readonly string[]).includes(value);
}

/** A calendar day as typed, `YYYY-MM-DD`: a date with no time and no zone, so nothing is shifted. */
export const CALL_SUMMARY_DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/** The day when it is a real calendar day in `YYYY-MM-DD`, else null. Never parses prose. */
export function readCallSummaryDate(raw: string): string | null {
  if (!CALL_SUMMARY_DAY_SHAPE.test(raw)) return null;
  const [y, m, d] = raw.split("-").map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  if (at.getUTCFullYear() !== y || at.getUTCMonth() !== m - 1 || at.getUTCDate() !== d) return null;
  return raw;
}

/** Only an attended consult has a call to summarize. */
export function callSummaryAllowed(appointmentStatus: string): boolean {
  return appointmentStatus === "attended";
}
